import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Input } from '../components/Input';
import { useResetPasswordMutation } from '../lib/queries';
import { TOAST_DURATION } from '../lib/constants';
import { NewPasswordForm, ResetPasswordSchema } from '../lib/schemas';
import { PATHS } from '../lib/paths';

export default function ResetPasswordPage() {
  const { t } = useTranslation();
  const { token } = useParams();
  const navigate = useNavigate();

  const resetPasswordMutation = useResetPasswordMutation();

  const { register, handleSubmit, formState } = useForm<NewPasswordForm>({
    mode: 'all',
    resolver: zodResolver(ResetPasswordSchema),
  });

  if (!token) return null;

  const onSubmit = (data: NewPasswordForm) => {
    resetPasswordMutation.mutate(
      { password: data.passwordNew, token: token.replaceAll('&', '.') },
      {
        onSuccess: () => {
          toast.success(t('settings.passwordChanged'), { duration: TOAST_DURATION });
          navigate(PATHS.loginPath());
        },
        onError: (error: any) => {
          toast.error(
            t('settings.passwordChangeError', { message: t(`apiError.${error.message}`) }),
            {
              duration: TOAST_DURATION,
            },
          );
        },
      },
    );
  };

  return (
    <main className="grid place-items-center h-full !m-0">
      <form method="post" className="grid gap-2 w-72 mx-auto p-4 rounded font-semibold space-y-4">
        <Input
          type="password"
          {...register('passwordNew', { required: true })}
          label={t('settings.passwordNew')}
          placeholder="password"
          error={formState.errors.passwordNew?.message?.toString()}
          required
        />

        <Input
          type="password"
          {...register('passwordConfirm', { required: true })}
          label={t('settings.passwordConfirm')}
          placeholder="password"
          error={formState.errors.passwordConfirm?.message?.toString()}
          required
        />
        <div className="flex justify-end items-center gap-2 mt-4">
          <button
            className="primary-fill"
            type="submit"
            disabled={!formState.isValid}
            onClick={handleSubmit(onSubmit)}
          >
            {t('common.save')}
          </button>
        </div>
      </form>
    </main>
  );
}
